import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cssVar } from '@/lib/theme/cssVar';
import { pointOnPathAtX } from '@/lib/svg-path';
import { signedUsd } from '@/lib/trading/format';
import type { PlaeePnlDataPointDto } from '@/lib/models';

export interface PortfolioPnlChartProps {
  /** Realized-PnL series from `GET /plaee/positions/pnl` (daily points, ascending). */
  points: PlaeePnlDataPointDto[];
}

const PAD_X = 10;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
/** Half the tooltip pill's rendered width — keeps the clamp honest if padding/font change. */
const TOOLTIP_CLAMP_LEFT = 46;
const TOOLTIP_CLAMP_RIGHT = 50;

/** `"2026-07-12"` → `"Jul 12"` for the axis and the hover tooltip. */
function fmtDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Catmull-Rom → cubic-bezier smooth path through the points. */
function smoothPath(pts: [number, number][]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/**
 * Portfolio realized-PnL chart — the "Aurora fill" treatment: a smooth
 * violet→magenta gradient stroke over a layered brand-purple fill that fades
 * to nothing, a dashed break-even line, four date ticks, a crosshair tooltip
 * (value + day) and a pulsing marker on the latest point.
 *
 * Hand-rolled SVG rather than lightweight-charts: the chosen design needs a
 * gradient stroke *along the path*, which the library can't draw, and the
 * series is small (daily points, no pan/zoom). Dates are used as given — no
 * client-clock stamping. The draw-in animation respects reduced-motion.
 */
export function PortfolioPnlChart({ points }: PortfolioPnlChartProps) {
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const linePathRef = useRef<SVGPathElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  // Cursor-anchored hover: crosshair x, the on-curve dot, the curve's value
  // there, and the nearest day for the tooltip's date part. `forData` pins the
  // sample to the series it was measured against — a refreshed series makes it
  // stale (dateIdx/curve belong to the old data), so rendering ignores it.
  const [rawHover, setRawHover] = useState<{
    x: number;
    y: number;
    value: number;
    dateIdx: number;
    forData: unknown;
  } | null>(null);

  // Measure the container so the SVG renders in pixel space (no text distortion).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setSize({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const data = useMemo(
    () => [...points].sort((a, b) => (a.date < b.date ? -1 : 1)).map((p) => ({ date: p.date, value: p.cumulativePnl })),
    [points],
  );

  // 0×0 until the ResizeObserver reports — geo stays null, nothing renders,
  // and the draw-in never plays against a guessed size.
  const { width, height } = size ?? { width: 0, height: 0 };
  const geo = useMemo(() => {
    if (data.length < 2 || width === 0 || height === 0) return null;
    const values = data.map((p) => p.value);
    // Anchor the domain on break-even when it's near the data (the design's
    // whole point); skip the zero line when the series lives far away from it.
    let min = Math.min(...values);
    let max = Math.max(...values);
    const zeroNear = 0 >= min - (max - min) * 0.5 && 0 <= max + (max - min) * 0.5;
    if (zeroNear) {
      min = Math.min(min, 0);
      max = Math.max(max, 0);
    }
    if (max === min) {
      max += 1;
      min -= 1;
    }
    const x = (i: number) => PAD_X + (i / (data.length - 1)) * (width - 2 * PAD_X);
    const y = (v: number) => PAD_TOP + (1 - (v - min) / (max - min)) * (height - PAD_TOP - PAD_BOTTOM);
    const valueAtY = (py: number) => min + (1 - (py - PAD_TOP) / (height - PAD_TOP - PAD_BOTTOM)) * (max - min);
    const pts: [number, number][] = data.map((p, i) => [x(i), y(p.value)]);
    const line = smoothPath(pts);
    const area = `${line} L ${x(data.length - 1)} ${height - PAD_BOTTOM} L ${x(0)} ${height - PAD_BOTTOM} Z`;
    // Four date ticks: first, last, and two evenly between.
    const tickIdx = [0, Math.round((data.length - 1) / 3), Math.round(((data.length - 1) * 2) / 3), data.length - 1];
    return { x, y, valueAtY, pts, line, area, zeroY: zeroNear ? y(0) : null, tickIdx: [...new Set(tickIdx)] };
  }, [data, width, height]);

  // Draw-in animation, once per SERIES (skipped for reduced motion). Keyed on
  // the data identity, not the path string — a container resize regenerates the
  // geometry but must not replay the reveal.
  const animatedForRef = useRef<typeof data | null>(null);
  useEffect(() => {
    const path = linePathRef.current;
    if (!path || !geo) return;
    if (animatedForRef.current === data) {
      // Same series re-laid-out (resize/breakpoint): the dash state from the
      // old path length would render gaps in the new path — clear it, no replay.
      path.style.transition = 'none';
      path.style.strokeDasharray = 'none';
      path.style.strokeDashoffset = '0';
      return;
    }
    animatedForRef.current = data;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const length = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = `${length}`;
    path.style.strokeDashoffset = `${length}`;
    // Flush, then animate the offset to zero.
    void path.getBoundingClientRect();
    path.style.transition = 'stroke-dashoffset 1100ms ease-out';
    path.style.strokeDashoffset = '0';
  }, [geo, data]);

  // Theme tokens read once — stable for the component's lifetime (matches the
  // codebase's chart convention), and keeps getComputedStyle out of the hover path.
  const colors = useMemo(
    () => ({
      accent: cssVar('--color-accent') || '#d239e8',
      accentDark: cssVar('--color-accent-dark') || '#9425c9',
      purple: cssVar('--color-purple') || '#721cb7',
      deep: cssVar('--color-brand-grad-deep') || '#5503ac',
      surface: cssVar('--color-surface') || '#ffffff',
    }),
    [],
  );
  const { accent, accentDark, purple, deep } = colors;

  // The measured container must render unconditionally — the ResizeObserver
  // observes it, and geometry only exists after the first measurement lands.
  const lastPoint = geo ? geo.pts[data.length - 1] : null;
  const hover = rawHover && rawHover.forData === data ? rawHover : null;
  const tooltipX = hover ? Math.min(Math.max(hover.x, TOOLTIP_CLAMP_LEFT), width - TOOLTIP_CLAMP_RIGHT) : 0;

  // Hover follows the cursor x continuously (no snapping to day columns): the
  // dot is sampled off the rendered curve so it glides along it, the value is
  // the curve's value there, and only the tooltip's date snaps (days are
  // discrete buckets). DOM sampling stays in the handler — refs during render
  // are off-limits.
  function handleMove(e: React.PointerEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || !geo) return;
    const px = Math.max(PAD_X, Math.min(e.clientX - rect.left, width - PAD_X));
    const dateIdx = Math.round(((px - PAD_X) / (width - 2 * PAD_X)) * (data.length - 1));
    const path = linePathRef.current;
    if (path) {
      const pt = pointOnPathAtX(path, px);
      setRawHover({ x: px, y: pt.y, value: geo.valueAtY(pt.y), dateIdx, forData: data });
    } else {
      setRawHover({ x: geo.x(dateIdx), y: geo.y(data[dateIdx].value), value: data[dateIdx].value, dateIdx, forData: data });
    }
  }

  return (
    <div ref={containerRef} className="absolute inset-0" onPointerMove={handleMove} onPointerLeave={() => setRawHover(null)}>
      {geo && (
        <svg width={width} height={height} className="block">
          <defs>
            <linearGradient id={`${gradientId}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={accent} stopOpacity="0.38" />
              <stop offset="0.55" stopColor={accentDark} stopOpacity="0.12" />
              <stop offset="1" stopColor={deep} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={`${gradientId}-stroke`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={purple} />
              <stop offset="1" stopColor={accent} />
            </linearGradient>
          </defs>

          {geo.zeroY != null && (
            <>
              <line
                x1={PAD_X}
                x2={width - PAD_X}
                y1={geo.zeroY}
                y2={geo.zeroY}
                stroke="currentColor"
                className="text-primary/30"
                strokeWidth={1}
                strokeDasharray="3 4"
              />
              <text
                x={width - PAD_X}
                y={geo.zeroY - 5}
                textAnchor="end"
                fontSize={9}
                fontWeight={600}
                fill="currentColor"
                className="text-primary/30"
              >
                $0
              </text>
            </>
          )}

          {geo.tickIdx.map((i, n) => (
            <text
              key={i}
              x={geo.x(i)}
              y={height - 8}
              textAnchor={n === 0 ? 'start' : n === geo.tickIdx.length - 1 ? 'end' : 'middle'}
              fontSize={9.5}
              fontWeight={500}
              fill="currentColor"
              className="text-primary/30"
            >
              {fmtDay(data[i].date)}
            </text>
          ))}

          <path d={geo.area} fill={`url(#${gradientId}-fill)`} />
          <path
            ref={linePathRef}
            d={geo.line}
            fill="none"
            stroke={`url(#${gradientId}-stroke)`}
            strokeWidth={2.5}
            strokeLinecap="round"
          />

          {hover && (
            <>
              <line
                x1={hover.x}
                x2={hover.x}
                y1={PAD_TOP}
                y2={height - PAD_BOTTOM}
                stroke="currentColor"
                className="text-primary/25"
                strokeWidth={1}
                strokeDasharray="2 3"
              />
              <circle cx={hover.x} cy={hover.y} r={4.5} fill={accentDark} stroke={colors.surface} strokeWidth={2} />
            </>
          )}
        </svg>
      )}

      {/* Pulsing marker on the latest point. */}
      {lastPoint && (
        <span
          aria-hidden
          className="pointer-events-none absolute flex size-3 -translate-x-1/2 -translate-y-1/2"
          style={{ left: lastPoint[0], top: lastPoint[1] }}
        >
          <span className="absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex size-3 rounded-full border-2 border-surface bg-gradient-to-b from-accent to-accent-dark" />
        </span>
      )}

      {/* Crosshair tooltip: "+$4.50 · Jul 12". */}
      {hover && (
        <div
          className="pointer-events-none absolute top-2 -translate-x-1/2 whitespace-nowrap rounded-pill border border-border bg-surface px-2.5 py-0.5 text-[11.5px] font-bold text-brand-grad-deep shadow-card tabular-nums"
          style={{ left: tooltipX }}
        >
          {signedUsd(hover.value)} <span className="font-medium text-muted">· {fmtDay(data[hover.dateIdx].date)}</span>
        </div>
      )}
    </div>
  );
}
